/**
 * Bridges legacy `cart:update` (ritual ATC, hotspots, quiz, quick-order)
 * → Horizon `CartLinesUpdateEvent` so cart-items / cart-icon / cart-drawer morph live.
 *
 * IMPORTANT: Horizon 4.1.x stripped `ThemeEvents.cartUpdate` from `@theme/events`.
 * Always listen to the literal event name — never ThemeEvents.cartUpdate (undefined live).
 *
 * `product-form-component` already dispatches CartLinesUpdateEvent itself — skipped here.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** @type {string} Legacy cart update name (still emitted by ritual/hotspots/quiz). */
const LEGACY_CART_UPDATE = 'cart:update';

const FALLBACK_SECTION_ID = 'cart-drawer-section';

function rootPath() {
  return window.Shopify?.routes?.root || '/';
}

/**
 * @returns {string}
 */
function getDrawerSectionId() {
  const oldInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
  const sectionEl =
    oldInner?.closest('.shopify-section') ||
    document.getElementById(`shopify-section-${FALLBACK_SECTION_ID}`) ||
    document.querySelector('theme-drawer#cart-drawer')?.closest('.shopify-section') ||
    null;

  if (sectionEl?.id?.startsWith('shopify-section-')) {
    return sectionEl.id.slice('shopify-section-'.length);
  }

  return FALLBACK_SECTION_ID;
}

/**
 * @param {any} detail
 * @returns {Promise<any>}
 */
async function ensureFullCart(detail) {
  const resource = detail?.resource;
  if (
    resource &&
    typeof resource.item_count === 'number' &&
    typeof resource.token === 'string' &&
    typeof resource.currency === 'string' &&
    Array.isArray(resource.items)
  ) {
    return resource;
  }

  const response = await fetch(`${rootPath()}cart.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`cart.js failed: ${response.status}`);
  return response.json();
}

/**
 * @param {string} sectionId
 * @returns {Promise<{ cart: any, sections: Record<string, string> }>}
 */
async function fetchSectionsViaCartUpdate(sectionId) {
  const response = await fetch(`${rootPath()}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    cache: 'no-store',
    body: JSON.stringify({
      sections: sectionId,
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) {
    throw new Error(`cart/update.js sections failed: ${response.status}`);
  }

  const data = await response.json();
  return {
    cart: data,
    sections: data?.sections && typeof data.sections === 'object' ? data.sections : {},
  };
}

/**
 * @param {Record<string, string> | null | undefined} sections
 * @param {string} sectionId
 * @param {any} cart
 */
function sectionsLookCurrent(sections, sectionId, cart) {
  const html = sections?.[sectionId];
  if (typeof html !== 'string' || !html.includes('data-hydration-key')) return false;
  if (!cart || Number(cart.item_count) <= 0) return true;
  return !html.includes('cart-drawer--empty') && !html.includes('data-cart-drawer-empty');
}

/**
 * @param {any} detail
 * @returns {{ merchandiseId: string, quantity: number }[]}
 */
function linesFromDetail(detail) {
  const variantId = detail?.data?.variantId;
  if (variantId) {
    return [{ merchandiseId: String(variantId), quantity: Number(detail?.data?.itemCount) || 1 }];
  }

  const items = detail?.resource?.items;
  if (Array.isArray(items) && items.length) {
    return items.slice(0, 20).map((item) => ({
      merchandiseId: String(item.variant_id ?? item.id),
      quantity: Number(item.quantity) || 1,
    }));
  }

  return [{ merchandiseId: '0', quantity: Number(detail?.data?.itemCount) || 1 }];
}

/**
 * @param {Event} event
 */
async function onLegacyCartUpdate(event) {
  const detail = /** @type {any} */ (event).detail;
  const source = detail?.data?.source;

  // Official emitters already fire CartLinesUpdateEvent
  if (source === 'cart-items-component') return;
  if (source === 'product-form-component') return;
  if (source === 'cart-legacy-bridge') return;
  if (detail?.data?.didError) return;

  const deferred = CartLinesUpdateEvent.createPromise();
  const lines = linesFromDetail(detail);

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines,
      promise: deferred.promise,
    })
  );

  const run = async () => {
    const sectionId = getDrawerSectionId();
    const cart = await ensureFullCart(detail);
    /** @type {Record<string, string>} */
    let sections =
      detail?.data?.sections && typeof detail.data.sections === 'object' ? { ...detail.data.sections } : {};

    if (!sectionsLookCurrent(sections, sectionId, cart)) {
      const bundled = await fetchSectionsViaCartUpdate(sectionId);
      sections = { ...sections, ...bundled.sections };
      // Prefer cart payload from update.js when it includes item_count/token
      if (bundled.cart?.token && typeof bundled.cart.item_count === 'number') {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(bundled.cart),
          detail: {
            sections,
            itemCount: bundled.cart.item_count,
            source: 'cart-legacy-bridge',
            didError: false,
          },
        });
        return;
      }
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections,
        itemCount: cart.item_count,
        source: 'cart-legacy-bridge',
        didError: false,
      },
    });
  };

  const promise = run().catch((error) => {
    console.warn('[cart-legacy-bridge] Failed to refresh cart drawer', error);
    deferred.reject(error);
  });

  if (typeof detail?.waitUntil === 'function') {
    detail.waitUntil(promise);
  }

  await promise;
}

document.addEventListener(LEGACY_CART_UPDATE, onLegacyCartUpdate);
